<template>
	<div v-if="!isAuthor && loading" class="backdrop" aria-live="assertive">
		<div class="spinner-border" role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
	</div>

	<div class="planner" aria-live="polite">
		<DateSelector />
		<div v-if="selectedDayClasses.length !== 0" class="results">
			<div class="container">
				<div class="my-1 text-body text-opacity-75 h6">{{ selectedDayClasses.length }} Results</div>
			</div>
		</div>
		<TransitionGroup name="slide-down">
			<ClassCard v-for="c in selectedDayClasses" :key="c.id" :class-data="c" />
		</TransitionGroup>
		<div v-if="selectedDayClasses.length === 0 && !loading" class="no-results container">
			<div class="row copy">
				<div class="col-lg-3">No results found</div>
				<div class="col-lg-6 mt-5 mt-lg-0">
					Hmmm. Something's not right.<br />
					Please reset your filters or try again.<br />
					<button
						class="btn btn-outline-dark mt-3 mt-lg-6"
						data-testid="reset-filters"
						@click="resetFilters()"
					>
						Reset Filters
					</button>
				</div>
			</div>
		</div>
		<div v-if="!loading" class="container">
			<div class="row">
				<div class="col">
					<!-- eslint-disable-next-line vue/no-v-html -->
					<div class="disclaimer" v-html="authoredContent.terms"></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useScheduleStore } from '@/stores/schedule';
import DateSelector from './DateSelector.vue';
import ClassCard from './ClassCard.vue';
import dayjs from 'dayjs';
import { getAuthoredContent } from '@/utils/getAuthoredContent';
import { prerenderGateCheck } from '@/utils/prerenderGateCheck';
const scheduleStore = useScheduleStore();
const { selectedDayClasses, loading } = storeToRefs(scheduleStore);
const { getData, resetFilters } = scheduleStore;

interface IAuthoredVariation {
	terms?: string;
}

const authoredContent: Ref<IAuthoredVariation> = ref({});

const isAuthor = window.lt.author;

onBeforeMount(() => {
	authoredContent.value = getAuthoredContent<IAuthoredVariation>('AempschedClassScheduleScript');
	const sunday = dayjs().clone().startOf('week').day(0);
	const saturday = dayjs().clone().startOf('week').day(6);
	if (prerenderGateCheck()) {
		return;
	}
	getData(sunday, saturday);
});
</script>

<style lang="scss" scoped>
$class-sched-border-color: rgba($border-color, 0.15);
.results {
	border-bottom: solid 1px $class-sched-border-color;
}

.no-results {
	padding-top: 3rem;
	padding-bottom: 20.5rem;
	border-bottom: solid 1px $class-sched-border-color;
}

.disclaimer {
	font-size: $font-size-sm;
	margin-top: map-get($spacers, 5);
	margin-bottom: 6rem;

	@include media-breakpoint-up(lg) {
		margin-top: 9.5rem;
		margin-bottom: 8rem;
	}
}

.backdrop {
	@include overlay-backdrop(9999, rgba($black, 0.2), 0.2);
	display: flex;
	align-items: center;
	justify-content: center;
}

// Used in Class Card Transition Group
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.5s ease;
}

.slide-down-enter-active {
	transition-delay: 0.5s;
}

.slide-down-enter-from,
.slide-down-leave-to {
	transform: translateY(-10px);
	opacity: 0;
}
</style>
